import { IFileResponse, UserShortForChatResponse } from '@api/client';
import { MESSAGE_TYPE } from '@chat/interfaces/chat.interfaces';
import type { MessageData, MessageReactionEntry } from '@chat/interfaces/chat-message-data.interfaces';
import type { ChatReactionNavigationSocketEvent } from '@chat/interfaces/reaction-navigation.interfaces';
import { ReplyStatus } from '@reply/reply.constants';
import type {
  CallCustomerReady,
  CallCustomerReadyReconnect,
  CallExit,
  CallExitFromBackend,
  CallPartnerConnectedStatus,
  CallRequestReconnect,
  CallResponse,
  CallStreamerReady,
} from './call.socket.interfaces';

export const enum SOCKET_EVENT_NAMES {
  INIT = 'init',
  LOG = 'log',
  ERROR = 'err',
  ANSWER = 'answer',
  SOCKET_REPLACED = 'socketReplaced',
  MESSAGE = 'message',
  GET_HISTORY = 'getHistory',
  READ = 'read',
  DELIVERED = 'delivered',
  UNDELIVERED = 'undelivered',
  REPLY_STATUS_CHANGED = 'replyStatusChanged',
  TASK_CREATED = 'taskCreated',
  QTY_UNREAD_MESSAGES = 'qtyUnreadMessages',
  CHAT_CREATED = 'chatCreated',
  DATA_UPDATED = 'dataUpdated',
  WALLET_TRANSFER_UPDATED = 'walletTransferUpdated',
  CHAT_UNREAD_UPDATED = 'chatUnreadUpdated',
  REACTION_NAVIGATION_UPDATED = 'reactionNavigationUpdated',
  NOTIFICATION = 'notification',
  FILE_UPLOADED = 'fileUploaded',
  AVATAR_SYNC_STATUS = 'avatarSyncStatus',
  AVATAR_STATUS_UPDATED = 'avatarStatusUpdated',
  AVATAR_PHONE_INFO_REQUEST = 'avatarPhoneInfoRequest',
  AVATAR_PHONE_INFO_RESPONSE = 'avatarPhoneInfoResponse',
  AVATAR_REQUEST = 'avatarRequest',
  AVATAR_RESPONSE = 'avatarResponse',
  AVATAR_CUSTOMER_READY = 'avatarCustomerReady',
  AVATAR_OFFER = 'avatarOffer',
  AVATAR_ANSWER = 'avatarAnswer',
  AVATAR_CANDIDATE = 'avatarCandidate',
  AVATAR_MESSAGE = 'avatarMessage',
  AVATAR_PARTNER_CONNECTED_STATUS = 'avatarPartnerConnectedStatus',
  AVATAR_EXIT = 'avatarExit',
  AVATAR_EXIT_FROM_BACKEND = 'avatarExitFromBackend',
  AVATAR_REQUEST_RECONNECT = 'avatarRequestReconnect',
  AVATAR_CUSTOMER_READY_RECONNECT = 'avatarCustomerReadyReconnect',
  AVATAR_ZOOM = 'avatarZoom',
  AVATAR_STATS = 'avatarStats',
  AVATAR_SAVE_DATA = 'avatarSaveData',
  AVATAR_WEBRTC_STATUS = 'avatarWebrtcStatus',
  AVATAR_VIEWER_READY = 'avatarViewerReady',
  AVATAR_STREAMER_READY = 'avatarStreamerReady',
  AUDIO_CALL_RESPONSE = 'audioCallResponse',
  AUDIO_CALL_CUSTOMER_READY = 'audioCallCustomerReady',
  AUDIO_CALL_OFFER = 'audioCallOffer',
  AUDIO_CALL_ANSWER = 'audioCallAnswer',
  AUDIO_CALL_CANDIDATE = 'audioCallCandidate',
  AUDIO_CALL_EXIT = 'audioCallExit',
  AUDIO_CALL_EXIT_FROM_BACKEND = 'audioCallExitFromBackend',
  AUDIO_CALL_REQUEST_RECONNECT = 'audioCallRequestReconnect',
  AUDIO_CALL_CUSTOMER_READY_RECONNECT = 'audioCallCustomerReadyReconnect',
  AUDIO_CALL_DELIVERY_RECEIPT = 'audioCallDeliveryReceipt',
  AUDIO_CALL_WEBRTC_STATUS = 'audioCallWebrtcStatus',
  AUDIO_CALL_PARTNER_CONNECTED_STATUS = 'audioCallPartnerConnectedStatus',
  AUDIO_CALL_STREAMER_READY = 'audioCallStreamerReady',
  EVENT_FOR_DEV = 'eventForDev',
  JOIN_STREAM_ROOM = 'joinStreamRoom',
  LEAVE_STREAM_ROOM = 'leaveStreamRoom',
  STREAM_MESSAGE = 'streamMessage',
  STREAM_LIKES = 'streamLikes',
  STREAM_LIVE = 'streamLive',
  STREAM_NOTICED = 'streamNoticed',
  STREAM_LIVE_VIEWERS = 'streamLiveViewers',
  STREAM_WATCHES = 'streamWatches',
  STREAM_BAN_USER = 'streamBanUser',
  STREAM_BAN_APPLIED = 'streamBanApplied',
  CHAT_PRESENCE_INIT = 'chatPresenceInit',
  CHAT_PRESENCE_UPDATE = 'chatPresenceUpdate',
  CHAT_PRESENCE_ACTIVITY = 'chatPresenceActivity',
  CHAT_TYPING = 'chatTyping',
  CHAT_TYPING_SNAPSHOT = 'chatTypingSnapshot',
  CHAT_TYPING_UPDATE = 'chatTypingUpdate',
  CHAT_TYPING_VIEW = 'chatTypingView',
  CHAT_USER_UPDATED = 'chatUserUpdated',
  READERS = 'readers',
}

export interface SocketError {
  code: number,
  message: string,
  lastMethod: string,
  lastData: any
}

export type WalletTransferEventType =
  | 'payment_intent.submitted'
  | 'payment_intent.finalized'
  | 'payment_intent.failed'
  | 'payment_intent.expired';

export interface EventWalletTransferUpdated {
  event_id: string;
  event_type: WalletTransferEventType;
  payment_intent_id: string;
  user_id: number;
}

export interface SocketDeliveryAck {
  response: boolean;
  delivered: boolean;
  retryable: boolean;
  code: number;
  status: string;
  message: string;
}

export type AudioCallReliableEventName =
  | SOCKET_EVENT_NAMES.AUDIO_CALL_RESPONSE
  | SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY
  | SOCKET_EVENT_NAMES.AUDIO_CALL_OFFER
  | SOCKET_EVENT_NAMES.AUDIO_CALL_ANSWER
  | SOCKET_EVENT_NAMES.AUDIO_CALL_CANDIDATE
  | SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT
  | SOCKET_EVENT_NAMES.AUDIO_CALL_EXIT_FROM_BACKEND
  | SOCKET_EVENT_NAMES.AUDIO_CALL_PARTNER_CONNECTED_STATUS
  | SOCKET_EVENT_NAMES.AUDIO_CALL_REQUEST_RECONNECT
  | SOCKET_EVENT_NAMES.AUDIO_CALL_CUSTOMER_READY_RECONNECT;

export type AudioCallDeliveryStatus = 'processed' | 'duplicate' | 'ignored' | 'failed';

export interface AudioCallReliableMeta {
  message_id: string;
  event: AudioCallReliableEventName;
  from_id: number;
  to_id: number;
  order_id?: number;
  source_socket_id?: string;
}

export interface AudioCallReliableEnvelope<TPayload = unknown> extends AudioCallReliableMeta {
  payload: TPayload;
}

export interface AudioCallDeliveryReceipt extends AudioCallReliableMeta {
  status: AudioCallDeliveryStatus;
  error?: string;
}

export interface AudioCallReliableSendResult {
  message_id: string;
  ack: SocketDeliveryAck | null;
  receipt: AudioCallDeliveryReceipt | null;
  delivered: boolean;
  retryable: boolean;
  status: string;
  message: string;
}

export interface EmitRead {
  chat_id: number;
  messages: string[];
}

export interface EmitChatTyping {
  chat_id: number;
  is_typing: boolean;
}

export interface EmitChatTypingView {
  chat_id: number;
  is_open: boolean;
}

export interface MessageUserInfo {
  id?: number;
  name?: string;
  surname?: string;
  avatar?: IFileResponse;
  official?: boolean;
}

export interface EventRead {
  messages: any;
  chat_id: number;
  readBy: number;
  readAt?: string;
}

export interface EmitMessage {
  chat_id: number;
  to_id?: number | null;
  message: string;
  file_ids: number[];
  guid: string;
  reply_message_id: string | null;
  data?: MessageData | null;
}

export interface EventMessage {
  chat_id: number;
  created_at: string;
  filesLength?: number;
  files?: IFileResponse[];
  from_id?: number;
  message?: string;
  to_id?: number;
  type: MESSAGE_TYPE;
  data?: MessageData | null;
  user_info?: MessageUserInfo;
  projection_update?: boolean;
}

export interface EventAnswer {
  type: string;
  response?: boolean;
  message: string;
  error?: unknown;
}

export interface SocketInitPayload {
  token: string;
  device_id: string;
  app_session_id: string;
}

export interface SocketReplacedEvent {
  device_id: string;
  app_session_id: string;
  socket_id: string;
}

export interface EventChatCreated {
  chatId: number;
}

export interface EventReplyStatusChanged {
  chatId: number;
  replyStatus: ReplyStatus;
}

export interface EventFileUploaded {
  file: IFileResponse;
  client_guid?: string;
  clientGuid?: string;
}

export interface ChatPreviewSocketPatch {
  last_message_id?: string | null;
  last_message_text?: string | null;
  last_message_type?: number | null;
  last_message_at?: string | null;
  last_message_user?: UserShortForChatResponse | null;
  user_1_last_message?: string | null;
  user_1_last_message_at?: string | null;
  user_2_last_message?: string | null;
  user_2_last_message_at?: string | null;
}

export interface ChatUnreadSocketPatch {
  user_1_qty_unread_messages?: number;
  user_2_qty_unread_messages?: number;
  my_unread_count?: number | null;
}

export interface MessageDeleteDataUpdatedPayload {
  action: 'delete';
  chat_id: number;
  message_id: string;
  scope?: 'me' | 'everyone';
  chat_preview?: ChatPreviewSocketPatch;
}

export interface MessageEditDataUpdatedPayload {
  action: 'edit';
  chat_id: number;
  message_id: string;
  message: string;
  data?: MessageData | null;
  chat_preview?: ChatPreviewSocketPatch;
  edited_at?: string | null;
  updated_at?: string | null;
}

export interface MessageReactionsDataUpdatedPayload {
  action: 'reactions';
  chat_id: number;
  message_id: string;
  data?: (MessageData & { reactions?: MessageReactionEntry[] | null }) | null;
  updated_at?: string | null;
}

export interface ReactionNavigationSocketUpdate {
  type: SOCKET_EVENT_NAMES.REACTION_NAVIGATION_UPDATED;
  data: ChatReactionNavigationSocketEvent;
}

export interface ChatUnreadUpdatedSocketEvent {
  chat_id: number;
  unread_patch: ChatUnreadSocketPatch;
}

export interface FileUploadEvent {
  type: string,
  data: EventFileUploaded
}

export interface EventAvatarSyncStatus {
  is_online: boolean;
}

export interface EventAvatarStatusUpdated {
  user_id: number;
  status: number;
}

export interface PhoneInfoRequest {
  to_id: number;
}

export interface PhoneInfoRequestExt extends PhoneInfoRequest {
  from_id: number;
}

export interface PhoneInfoResponse {
  to_id: number;
  battery_level: number;
  is_charging: boolean;
  phone_model: string;
}

export interface PhoneInfoResponseExt extends PhoneInfoResponse {
  from_id: number;
}

export type EventAvatarResponse = CallResponse;

export type EventAvatarCustomerReady = CallCustomerReady;

export type EventPartnerConnectedStatus = CallPartnerConnectedStatus;

export type EventExit = CallExit;

export type EventExitFromBackend = CallExitFromBackend;

export type EventAvatarRequestReconnect = CallRequestReconnect;

export type EventAvatarCustomerReadyReconnect = CallCustomerReadyReconnect;

export interface EventAvatarViewerReady {
  from_id: number;
}

export type EventAvatarStreamerReady = CallStreamerReady;

export interface EventStreamLikes {
  video_id: number;
  likes: number;
  dislikes: number;
}

export interface EventStreamLive {
  video_id: number;
  live: boolean;
}

export interface EventStreamNoticed {
  video_id: number;
  video_user_id: number;
  subscribers_noticed: number;
}

export interface EventStreamLiveViewers {
  video_id: number;
  live_viewers: number;
}

export interface EventStreamWatches {
  video_id: number;
  watches: number;
}

export interface EventStreamMessage {
  video_id: number;
  from_id: number;
  avatar: IFileResponse;
  name: string,
  surname: string;
  message: string;
  official: boolean;
  optimistic?: boolean;
}

export interface StreamBanUserInfo {
  video_id: number;
  user_id: number;
  banned_by?: number;
  created_at?: string;
}

export type EventStreamBanApplied = StreamBanUserInfo

export interface ChatPresenceUser {
  userId: number;
  isOnline: boolean;
  lastActivityAt?: string | null;
}

export interface ChatPresenceState {
  chatId: number;
  users: ChatPresenceUser[];
}

export interface ChatPresenceUpdate {
  chatId: number;
  userId: number;
  isOnline: boolean;
  lastActivityAt?: string | null;
}

export type ChatPresenceEvent =
  | { type: SOCKET_EVENT_NAMES.CHAT_PRESENCE_INIT; data: ChatPresenceState[] }
  | { type: SOCKET_EVENT_NAMES.CHAT_PRESENCE_UPDATE; data: ChatPresenceUpdate };

export interface ChatTypingUpdate {
  chatId: number;
  userId: number;
  isTyping: boolean;
  expiresAt?: string | null;
}

export interface ChatTypingSnapshotUser {
  userId: number;
  expiresAt?: string | null;
}

export interface ChatTypingSnapshot {
  chatId: number;
  users: ChatTypingSnapshotUser[];
}

export type ChatTypingEvent =
  | { type: SOCKET_EVENT_NAMES.CHAT_TYPING_UPDATE; data: ChatTypingUpdate }
  | { type: SOCKET_EVENT_NAMES.CHAT_TYPING_SNAPSHOT; data: ChatTypingSnapshot };
